import React, { Component } from 'react';
import { connect } from 'react-redux';
import AuthService from '../services/AuthService';
import StorageService from '../services/StorageService';
import { StorageConstants } from '../constants';
import { setUserCompanyList } from '../state/actions/company';
import { getCurrentUserSession } from '../state/actions/user';
import { resetContractStore, getUserContracts, updateContractInfoSilently } from '../state/actions/contract';
import { clearUserInfo, updateUserInfoFromToken } from '../state/actions/user';
import { clearCompanyStore } from '../state/actions/company';
import { navigate } from 'gatsby';
import { persistor } from '../App';
import ApiBuilder from '../helpers/ApiBuilder';
import { getAllPlatforms, getAllApiResources, getAllApplicationCategories } from '../state/actions/lookupData';  

const AuthContext = React.createContext();
const authService = new AuthService.authServiceInstance()

class AuthProvider extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loggedIn: false,
      signingOngoing: true,
      message: '',
      user: null,
      services: null,
      silentRenewFailed: false,
      isSigningOut: false,
    };
    this.authService = authService;
    this.getUser = this.getUser.bind(this);
    this.login = this.login.bind(this);
    this.logout = this.logout.bind(this);
    this.renewToken = this.renewToken.bind(this);
    this.updateUser = this.updateUser.bind(this);
    this.updateSilentRenewState = this.updateSilentRenewState.bind(this);
    this.onUserLogOut = this.onUserLogOut.bind(this);
    this.onUserLoaded = this.onUserLoaded.bind(this);

    this.authService.userManager.events.addUserSignedOut(this.onUserLogOut)
    this.authService.userManager.events.addUserLoaded(this.onUserLoaded)
  }

  componentDidMount() {
    this.getUser(true);
  }

  onUserLoaded() {
    this.getUser()
  }

  onUserLogOut() {
    ApiBuilder.setAccessTokenToHeader('');

    this.clearData()
    persistor.purge()

    // clear auth provider state
    this.setState({
      loggedIn: false,
      signingOngoing: false,
      message: '',
      user: null,
      services: null,
      silentRenewFailed: false,
      isSigningOut: false
    })
  }

  getUser(isFirstTime = false) {
    return this.authService.getUser()
      .then((user) => {
        if (user && !user.expired && user.access_token) {
          this.props.updateUserInfoFromToken(user.access_token);
          ApiBuilder.setAccessTokenToHeader(user.access_token);

          this.setState({
            loggedIn: true,
            signingOngoing: false,
            message: 'Successfully Logged In',
            user,
          });
          isFirstTime && this.props.updateContractInfoSilently();
          this.authService.userManager.clearStaleState();
        } else {
          this.setState({
            loggedIn: false,
            signingOngoing: false,
            message: 'Login failed',
            user: null,
          });
          this.onUserLogOut();
          this.authService.userManager.clearStaleState();
        }
        return user;
      });
  }

  login(routerSnapshot = '') {
    StorageService.setItem(StorageConstants.ROUTER_SNAPSHOT, routerSnapshot);
    return this.authService.login();
  }

  async logout() {
    try {
      // enable this to show the logout page
      this.setState({ isSigningOut: true })

      // call force logout url to invalidate session
      await this.authService.logoutSilently()
      await this.authService.removeUser()

      // clear redux state
      this.clearData()
      persistor.purge()

      // clear auth provider state
      this.setState({
        loggedIn: false,
        signingOngoing: false,
        message: '',
        user: null,
        services: null,
        silentRenewFailed: false,
      })

    }
    catch (e) { }
    finally {
      setTimeout(() => this.setState({ isSigningOut: false }), 500)
      navigate('/')
    }
  }

  // keeping previous method to fallback in any case
  // logout() {
  //   return this.authService.logout();
  // }

  renewToken() {
    return this.authService.renewToken();
  }

  updateUser(user) {
    if (!user || user.expired) {
      this.setState({
        loggedIn: false,
        signingOngoing: false,
        message: 'Login Failed',
        user: null,
        silentRenewFailed: false,
      });
      this.clearData()
      this.authService.userManager.clearStaleState();
    } else {
      this.setState({
        loggedIn: true,
        signingOngoing: false,
        message: 'Successfully Logged In',
        user,
        silentRenewFailed: false,
      });
      this.initializeData(user)
      this.authService.userManager.clearStaleState();
    }
  }

  initializeData = (user) => {
    this.props.setUserCompanyList();
    this.props.getUserContracts();
    this.props.updateUserInfoFromToken(user.access_token);
    this.props.getAllPlatforms()
    this.props.getAllApiResources()
    this.props.getAllApplicationCategories()
  }

  clearData = () => {
    this.props.resetContractStore();
    this.props.clearUserInfo();
    this.props.clearCompanyStore();
  }

  updateSilentRenewState(state = false) {
    this.setState({
      silentRenewFailed: state,
    });
  }

  render() {
    const state = {
      auth: {
        ...this.state,
        services: {
          login: this.login,
          logout: this.logout,
          renewToken: this.renewToken,
          getUser: this.getUser,
          updateUser: this.updateUser,
          updateSilentRenewState: this.updateSilentRenewState
        }
      }
    };
    const { children } = this.props;
    return (
      <AuthContext.Provider value={state}>
        {children}
      </AuthContext.Provider>
    );
  }
}

const mapStateToProps = () => {
  return {
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setUserCompanyList: () => dispatch(setUserCompanyList()),
    getCurrentUserSession: () => dispatch(getCurrentUserSession()),
    resetContractStore: () => dispatch(resetContractStore()),
    getUserContracts: () => dispatch(getUserContracts()),
    clearUserInfo: () => dispatch(clearUserInfo()),
    updateUserInfoFromToken: (token) => dispatch(updateUserInfoFromToken(token)),
    clearCompanyStore: () => dispatch(clearCompanyStore()),
    updateContractInfoSilently: () => dispatch(updateContractInfoSilently()),
    getAllPlatforms: () => dispatch(getAllPlatforms()),
    getAllApiResources: () => dispatch(getAllApiResources()),
    getAllApplicationCategories: () => dispatch(getAllApplicationCategories()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AuthProvider);

export const withAuth = (BaseComponent) => {
  class AuthComponent extends Component {
    render() {
      return (
        <AuthContext.Consumer>
          {context => (
            <BaseComponent
              {...this.props}
              auth={context ? context.auth : {
                loggedIn: false,
                userSigninFetching: false,
                userSigninError: '',
                user: null,
                isSigningOut: false,
                services: null,
              }}
            />
          )}
        </AuthContext.Consumer>
      )
    }
  }
  return AuthComponent;
};
